import { useEffect, useState } from "react";
import { Modal, Form, Button, Row, Col } from "react-bootstrap";

const ROLES = ["usuario", "admin"];

// Debe coincidir con el `permiso` de cada estrategia en Home.js / los
// programaRequerido de App.js / Registro.js. Se agrega acá cada vez que se
// construya una estrategia nueva.
export const ESTRATEGIAS_DISPONIBLES = [
  { value: "rbc", label: "♿ Discapacidad (RBC)" },
  { value: "vejez", label: "🧓 Vejez" },
  { value: "migrante", label: "🧭 Población Migrante" },
  { value: "habitante_calle", label: "🏙️ Habitante de Calle" },
];

const FORM_VACIO = {
  nombre: "",
  identificacion: "",
  email: "",
  numero_contrato: "",
  password: "",
  rol: "usuario",
  programas: [],
};

const TITULOS = {
  crear: "➕ Nuevo usuario",
  editar: "✏️ Editar usuario",
  aprobar: "✓ Aprobar solicitud de cuenta",
};

const BOTON = {
  crear: "Crear usuario",
  editar: "Guardar cambios",
  aprobar: "Aprobar cuenta",
};

// modo: "crear" | "editar" | "aprobar"
//  - aprobar: los datos personales vienen del registro público y se muestran
//    solo para revisión; el admin decide rol y programas definitivos.
export default function ModalUsuario({ abierto, modo, usuarioInicial, guardando, onGuardar, onCerrar }) {
  const [form, setForm] = useState(FORM_VACIO);

  useEffect(() => {
    if (!abierto) return;

    if ((modo === "editar" || modo === "aprobar") && usuarioInicial) {
      setForm({
        nombre: usuarioInicial.nombre || "",
        identificacion: usuarioInicial.identificacion || "",
        email: usuarioInicial.email || "",
        numero_contrato: usuarioInicial.numero_contrato || "",
        password: "", // no se edita contraseña aquí — eso va por "Restablecer contraseña"
        rol: usuarioInicial.rol || "usuario",
        programas: Array.isArray(usuarioInicial.programas) ? usuarioInicial.programas : [],
      });
    } else {
      setForm(FORM_VACIO);
    }
  }, [abierto, modo, usuarioInicial]);

  function actualizarCampo(campo, valor) {
    setForm((f) => ({ ...f, [campo]: valor }));
  }

  function manejarSubmit(e) {
    e.preventDefault();
    onGuardar(form);
  }

  const soloLectura = modo === "aprobar";

  return (
    <Modal show={abierto} onHide={onCerrar} centered scrollable backdrop={guardando ? "static" : true}>
      <Form onSubmit={manejarSubmit} className="d-flex flex-column" style={{ minHeight: 0, flex: 1 }}>
        <Modal.Header closeButton={!guardando}>
          <Modal.Title as="h5">{TITULOS[modo]}</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {soloLectura ? (
            <div className="p-3 mb-3" style={{ background: "#f6f8fb", borderRadius: 12, border: "1px solid #e3e8ef" }}>
              <div className="fw-bold" style={{ color: "#1a3a5c" }}>{form.nombre}</div>
              <div className="text-muted" style={{ fontSize: "0.82rem" }}>{form.email}</div>
              <div className="d-flex flex-wrap gap-3 mt-2" style={{ fontSize: "0.82rem" }}>
                <span><span className="text-muted">Identificación:</span> <strong>{form.identificacion}</strong></span>
                <span><span className="text-muted">Contrato:</span> <strong>{form.numero_contrato}</strong></span>
              </div>
              <div className="text-muted mt-2" style={{ fontSize: "0.76rem" }}>
                Las estrategias marcadas abajo son las que la persona solicitó — ajústalas antes de aprobar.
              </div>
            </div>
          ) : (
            <>
              <Form.Group className="mb-3">
                <Form.Label>Nombre completo</Form.Label>
                <Form.Control value={form.nombre} onChange={(e) => actualizarCampo("nombre", e.target.value)} required autoFocus />
              </Form.Group>

              <Row className="g-3 mb-3">
                <Col sm={6}>
                  <Form.Group>
                    <Form.Label>Identificación</Form.Label>
                    <Form.Control
                      value={form.identificacion}
                      onChange={(e) => actualizarCampo("identificacion", e.target.value)}
                      disabled={modo === "editar"} // llave del usuario — no se edita aquí
                      inputMode="numeric"
                      required
                    />
                  </Form.Group>
                </Col>
                <Col sm={6}>
                  <Form.Group>
                    <Form.Label>Número de contrato</Form.Label>
                    <Form.Control value={form.numero_contrato} onChange={(e) => actualizarCampo("numero_contrato", e.target.value)} required />
                  </Form.Group>
                </Col>
              </Row>

              <Form.Group className="mb-3">
                <Form.Label>Correo electrónico</Form.Label>
                <Form.Control type="email" value={form.email} onChange={(e) => actualizarCampo("email", e.target.value)} required />
              </Form.Group>

              {modo === "crear" && (
                <Form.Group className="mb-3">
                  <Form.Label>Contraseña inicial</Form.Label>
                  <Form.Control
                    type="password"
                    value={form.password}
                    onChange={(e) => actualizarCampo("password", e.target.value)}
                    minLength={6}
                    autoComplete="new-password"
                    required
                  />
                </Form.Group>
              )}
            </>
          )}

          <Form.Group className="mb-3">
            <Form.Label>Rol</Form.Label>
            <Form.Select value={form.rol} onChange={(e) => actualizarCampo("rol", e.target.value)}>
              {ROLES.map((r) => (
                <option key={r} value={r}>{r === "admin" ? "admin — ve y administra todo" : "usuario — solo sus estrategias"}</option>
              ))}
            </Form.Select>
          </Form.Group>

          {form.rol !== "admin" && (
            <Form.Group>
              <Form.Label>Programas (acceso a estrategias)</Form.Label>
              <div className="border rounded-3 p-3">
                <Form.Check
                  type="checkbox"
                  id="prog-todos"
                  label={<strong>TODOS</strong>}
                  checked={form.programas.includes("TODOS")}
                  onChange={(e) => actualizarCampo("programas", e.target.checked ? ["TODOS"] : [])}
                />
                <hr className="my-2" />
                {ESTRATEGIAS_DISPONIBLES.map((est) => (
                  <Form.Check
                    key={est.value}
                    id={`prog-${est.value}`}
                    type="checkbox"
                    className="py-1"
                    label={est.label}
                    disabled={form.programas.includes("TODOS")}
                    checked={form.programas.includes(est.value)}
                    onChange={(e) => {
                      const sinTodos = form.programas.filter((p) => p !== "TODOS");
                      actualizarCampo(
                        "programas",
                        e.target.checked ? [...sinTodos, est.value] : sinTodos.filter((p) => p !== est.value)
                      );
                    }}
                  />
                ))}
              </div>
              <Form.Text className="text-muted" style={{ fontSize: "0.76rem" }}>
                "TODOS" da acceso automático a cualquier estrategia nueva que se agregue después. Sin ningún programa
                marcado, el usuario no ve ninguna estrategia.
              </Form.Text>
            </Form.Group>
          )}
        </Modal.Body>

        <Modal.Footer className="d-flex gap-2 flex-nowrap">
          <Button variant="outline-secondary" className="flex-fill" onClick={onCerrar} type="button" disabled={guardando}>
            Cancelar
          </Button>
          <Button
            type="submit"
            className={`flex-fill ${modo === "aprobar" ? "" : "btn-ps"}`}
            variant={modo === "aprobar" ? "success" : undefined}
            disabled={guardando}
          >
            {guardando ? "Guardando..." : BOTON[modo]}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}
